import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../layout/Layout';

const services = [
  { path: '/web-application', icon: '/assets/images/icons/development.svg', title: 'Web Application', text: 'Revolutionize your online presence with our innovative web application, delivering seamless user experiences and unparalleled functionality.' },
  { path: '/mobile-application', icon: '/assets/images/icons/mobile-app.svg', title: 'Mobile Application', text: 'Elevate your on-the-go experience with our intuitive mobile application, empowering you to stay connected and productive anytime, anywhere.' },
  { path: '/digital-marketing', icon: '/assets/images/icons/digital-marketing.svg', title: 'Digital Marketing', text: "Unlock your brand's full potential with our dynamic digital marketing strategies, fostering growth and interaction across online platforms." },
  { path: '/devops', icon: '/assets/images/icons/devops.svg', title: 'Devops', text: 'Maximize efficiency and collaboration through our DevOps solutions, streamlining development and accelerating deployment cycles.' },
  { path: '/software-training', icon: '/assets/images/icons/trainer.svg', title: 'Software Training', text: 'Empower your team with expert-led software training tailored to your needs, unlocking their full potential and driving success in the digital age.' },
  { path: '/software-consulting', icon: '/assets/images/icons/discussion.svg', title: 'Software Consulting', text: "Navigate technology challenges confidently with our expert software consulting services designed to optimise and grow your business." },
];

/* ── Scroll-reveal hook: adds a class once an element enters the viewport ── */
function useRevealOnScroll(options = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px', ...options }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, revealed];
}

/* ── Wrapper that fades/slides content in on scroll, with optional stagger ── */
function Reveal({ children, as: Tag = 'div', delay = 0, className = '', style, ...rest }) {
  const [ref, revealed] = useRevealOnScroll();
  return (
    <Tag
      ref={ref}
      className={`svc-reveal ${revealed ? 'svc-revealed' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default function Services() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <Layout>

      <style>{`
        /* ══════════════════════════════════════
           SHARED REVEAL / UTILITY SYSTEM
        ══════════════════════════════════════ */
        .svc-reveal {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .svc-reveal.svc-revealed { opacity: 1; transform: translateY(0); }
        @media (prefers-reduced-motion: reduce) {
          .svc-reveal { opacity: 1; transform: none; transition: none; }
        }

        .svc-eyebrow {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          padding: 7px 18px 9px;
          border-radius: 50px;
          margin-bottom: 14px;
          background: rgba(0,165,181,0.12);
          color: #4DD8E0;
        }
        .svc-eyebrow::after {
          content: '';
          position: absolute;
          bottom: 3px; left: 18px;
          width: 14px; height: 2px;
          background: #D52B1E;
          border-radius: 2px;
          animation: svcEyebrowDash 2.4s ease-in-out infinite;
        }
        @keyframes svcEyebrowDash {
          0%, 100% { width: 14px; }
          50%      { width: 22px; }
        }

        /* ══════════════════════════════════════
           PAGE HEADER
        ══════════════════════════════════════ */
        .svc-header {
          position: relative;
          overflow: hidden;
        }
        .svc-header-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .svc-header-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
        }
        .svc-header-orb-1 {
          width: 500px; height: 500px;
          top: -180px; left: -100px;
          background: radial-gradient(circle, rgba(0,165,181,0.35) 0%, transparent 70%);
          animation: svcOrbDrift1 14s ease-in-out infinite alternate;
        }
        .svc-header-orb-2 {
          width: 420px; height: 420px;
          bottom: -160px; right: -80px;
          background: radial-gradient(circle, rgba(0,196,216,0.25) 0%, transparent 70%);
          animation: svcOrbDrift2 18s ease-in-out infinite alternate;
        }
        @keyframes svcOrbDrift1 { 0% { transform: translate(0,0); } 100% { transform: translate(50px, 30px); } }
        @keyframes svcOrbDrift2 { 0% { transform: translate(0,0); } 100% { transform: translate(-40px, -30px); } }
        .svc-header-particles { position: absolute; inset: 0; overflow: hidden; }
        .svc-header-particle {
          position: absolute;
          bottom: -20px;
          width: 4px; height: 4px;
          border-radius: 50%;
          background: rgba(0, 196, 216, 0.55);
          box-shadow: 0 0 8px rgba(0, 196, 216, 0.6);
          animation-name: svcParticleFloat;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        @keyframes svcParticleFloat {
          0%   { transform: translateY(0); opacity: 0; }
          10%  { opacity: 0.8; }
          90%  { opacity: 0.3; }
          100% { transform: translateY(-360px); opacity: 0; }
        }
        .svc-header-title {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .svc-header-title.svc-visible { opacity: 1; transform: translateY(0); }
        .svc-header-crumbs {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s;
        }
        .svc-header-crumbs.svc-visible { opacity: 1; transform: translateY(0); }
        .svc-crumb-link {
          position: relative;
        }
        .svc-crumb-link::after {
          content: '';
          position: absolute;
          left: 0; bottom: -2px;
          width: 0; height: 1px;
          background: currentColor;
          transition: width 0.25s ease;
        }
        .svc-crumb-link:hover::after { width: 100%; }

        /* ══════════════════════════════════════
           SERVICE CARDS
        ══════════════════════════════════════ */
        .svc-card {
          position: relative;
          overflow: hidden;
          transition: transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s ease;
        }
        .svc-card:hover {
          transform: translateY(-8px);
        }
        .svc-card-accent {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #00A5B5, #00d4ea);
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.4s ease;
        }
        .svc-card:hover .svc-card-accent { transform: scaleX(1); }
        .svc-card-shine {
          position: absolute;
          top: 0; left: -75%;
          width: 50%; height: 100%;
          background: linear-gradient(120deg, transparent, rgba(255,255,255,0.35), transparent);
          transform: skewX(-20deg);
          pointer-events: none;
        }
        .svc-card:hover .svc-card-shine { animation: svcShineSweep 0.9s ease; }
        @keyframes svcShineSweep { 0% { left: -75%; } 100% { left: 130%; } }
        .svc-card-icon-wrap {
          transition: transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
          animation: svcIconFloat 3.4s ease-in-out infinite;
        }
        @keyframes svcIconFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        .svc-card:hover .svc-card-icon-wrap {
          animation-play-state: paused;
          transform: rotate(-360deg) scale(1.08);
        }
        .svc-card-learn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .svc-card-learn span {
          display: inline-block;
          transition: transform 0.3s ease;
        }
        .svc-card-learn:hover span { transform: translateX(4px); }

        /* ══════════════════════════════════════
           FEATURE CARDS
        ══════════════════════════════════════ */
        .svc-feature-card { overflow: hidden; transition: transform 0.35s ease, box-shadow 0.35s ease; }
        .svc-feature-card:hover { transform: translateY(-6px); }
        .svc-feature-img-wrap { overflow: hidden; position: relative; }
        .svc-feature-img-wrap img { transition: transform 0.6s ease; display: block; }
        .svc-feature-card:hover .svc-feature-img-wrap img { transform: scale(1.1); }
        .svc-feature-badge {
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .svc-feature-card:hover .svc-feature-badge {
          transform: scale(1.15) rotate(10deg);
        }

        /* ══════════════════════════════════════
           MAP
        ══════════════════════════════════════ */
        .svc-map-wrap {
          position: relative;
          filter: grayscale(0.15);
          transition: filter 0.4s ease;
        }
        .svc-map-wrap:hover { filter: grayscale(0); }

        /* ══════════════════════════════════════
           CONTACT INFO
        ══════════════════════════════════════ */
        .svc-contact-item { transition: transform 0.3s ease; }
        .svc-contact-item:hover { transform: translateY(-4px); }
        .svc-contact-icon {
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.3s ease;
        }
        .svc-contact-item:hover .svc-contact-icon {
          transform: scale(1.12) rotate(-8deg);
        }

        /* ══════════════════════════════════════
           BRANCHES GRID (mobile-safe, no carousel dependency)
        ══════════════════════════════════════ */
        .svc-branch-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 20px;
          max-width: 480px;
          margin: 0 auto;
        }
        @media (max-width: 480px) {
          .svc-branch-grid {
            grid-template-columns: 1fr;
            max-width: 260px;
            gap: 28px;
          }
        }
        .svc-branch-item {
          transition: transform 0.3s ease;
          min-width: 0;
        }
        .svc-branch-flag {
          width: 96px;
          height: 96px;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), border-color 0.3s ease, box-shadow 0.3s ease;
        }
        @media (max-width: 480px) {
          .svc-branch-flag { width: 80px; height: 80px; }
        }
        .svc-branch-item:hover .svc-branch-flag {
          transform: scale(1.08) rotate(-4deg);
          box-shadow: 0 10px 24px rgba(0,165,181,0.25);
        }
        .svc-branch-name {
          transition: color 0.25s ease, transform 0.25s ease;
        }
        .svc-branch-item:hover .svc-branch-name {
          color: #00A5B5;
          transform: translateY(-2px);
        }
      `}</style>

      {/* Page Header */}
      <section className="svc-header relative overflow-hidden min-h-[280px] md:min-h-[360px] flex items-center bg-brand-dark">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40" style={{ backgroundImage: 'url(/assets/images/banner/service.jpg)' }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/30 to-brand/5"></div>
        <div className="svc-header-bg" aria-hidden="true">
          <div className="svc-header-orb svc-header-orb-1" />
          <div className="svc-header-orb svc-header-orb-2" />
          <div className="svc-header-particles">
            {Array.from({ length: 10 }).map((_, i) => (
              <span
                key={i}
                className="svc-header-particle"
                style={{ left: `${(i * 97) % 100}%`, animationDelay: `${i * 0.8}s`, animationDuration: `${7 + (i % 4) * 2}s` }}
              />
            ))}
          </div>
        </div>
        <div className="container relative z-10 py-20">
          <div className="text-center">
            <h2 className={`svc-header-title text-4xl md:text-5xl font-bold font-heading text-white mb-4 ${visible ? 'svc-visible' : ''}`}>OUR SERVICES</h2>
            <ul className={`svc-header-crumbs flex justify-center items-center gap-3 text-white/70 text-sm list-none p-0 m-0 ${visible ? 'svc-visible' : ''}`}>
              <li><Link className="svc-crumb-link hover:text-brand transition-colors" to="/">Home</Link></li>
              <li><span className="text-white/40">/</span></li>
              <li className="text-brand">Services</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="row gutter-y-30">
            {services.map(({ path, icon, title, text }, i) => (
              <div
                key={path}
                onClick={() => navigate(path)}
                className="col-xl-4 col-lg-6 col-md-6 cursor-pointer"
              >
                <Reveal delay={(i % 3) * 120} className="svc-card group bg-white rounded-2xl shadow-md hover:shadow-xl overflow-hidden border border-gray-100">
                  <span className="svc-card-accent" aria-hidden="true" />
                  <div className="bg-brand-dark/5 p-8 text-center group-hover:bg-brand transition-colors duration-300 relative overflow-hidden">
                    <span className="svc-card-shine" aria-hidden="true" />
                    <div className="svc-card-icon-wrap w-20 h-20 rounded-full bg-white shadow-md flex items-center justify-center mx-auto">
                      <img src={icon} className="w-10 h-10" alt={title} />
                    </div>
                    <h3 className="text-brand-dark group-hover:text-white font-bold font-heading text-xl mt-4 transition-colors">{title}</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-500 text-sm leading-relaxed text-center">{text}</p>
                    <div className="text-center mt-4">
                      <Link
                        to={path}
                        onClick={(e) => e.stopPropagation()}
                        className="svc-card-learn text-brand hover:text-brand-dark text-sm font-medium transition-colors"
                      >
                        Learn More <span>→</span>
                      </Link>
                    </div>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="row gutter-y-30">
         {[
              { img: '/security.jpg', icon: 'icon-privacy', title: 'Digital Security', desc: "Protect against evolving cyber threats in today's digital landscape" },
              { img:'/trusted-partners.png', icon: 'icon-certified', title: 'Trusted Agency', desc: 'Trusted partner in excellence with integrity and reliability' },
              { img: '/OIP.webp', icon: 'icon-laptop', title: 'IT Outsourcing', desc: 'Technological needs with expertise and efficiency' },
            ].map(({ img, icon, title, desc }, i) => (
              <div className="col-xl-4 col-lg-4" key={i}>
                <Reveal delay={i * 120} className="svc-feature-card bg-white rounded-2xl shadow-md hover:shadow-xl overflow-hidden">
                  <div className="svc-feature-img-wrap relative">
                    <img src={img} alt="#" className="w-full h-52 object-cover" />
                    <div className="svc-feature-badge absolute bottom-4 right-4 w-12 h-12 rounded-full bg-brand flex items-center justify-center shadow-lg">
                      <span className={`${icon} text-white text-xl`}></span>
                    </div>
                  </div>
                  <div className="p-6 text-center">
                    <h2 className="text-xl font-bold font-heading text-brand-dark mb-2">{title}</h2>
                    <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Map */}
      <Reveal as="section" className="svc-map-wrap w-full">
        <iframe
          src="https://maps.google.com/maps?q=44+Abelard+Ave,+Brampton,+ON+L6Y2K8,+Canada&t=&z=15&ie=UTF8&iwloc=&output=embed"
          className="w-full h-72 md:h-96 border-0 block"
          allowFullScreen=""
          title="Office map"
        ></iframe>
      </Reveal>

      {/* Contact Info */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <Reveal className="relative bg-white rounded-2xl shadow-xl overflow-hidden p-8 md:p-12">
            <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url(/assets/images/shapes/contact-info-v1-bg0.png)', backgroundSize: 'cover' }}></div>
            <div className="row relative z-10">
              {[
                { iconClass: 'icon-location-filled', label: 'Address', lines: ['44 Abelard Ave, Brampton, ON L6Y2K8, Canada.'] },
                { iconClass: 'icon-phone-auricular', label: 'Get In Touch', lines: ['+1(647)-687-4226'], hrefs: ['tel:+16476874226'] },
                { iconClass: 'icon-email2', label: 'Quick Email us', lines: ['magizhinitech@gmail.com'], hrefs: ['mailto:magizhinitech@gmail.com'] },
              ].map(({ iconClass, label, lines, hrefs }, i) => (
                <div className="col-xl-4 col-lg-4" key={i}>
                  <div className="svc-contact-item flex items-start gap-4 mb-8 xl:mb-0">
                    <div className="svc-contact-icon flex-shrink-0 w-14 h-14 rounded-full bg-brand flex items-center justify-center text-white text-xl">
                      <span className={iconClass}></span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-widest font-medium mb-1">{label}</p>
                      {lines.map((line, j) => (
                        hrefs ? (
                          <h3 key={j} className="text-brand-dark font-semibold text-sm leading-relaxed">
                            <a href={hrefs[j]} className="hover:text-brand transition-colors">{line}</a>
                          </h3>
                        ) : (
                          <h3 key={j} className="text-brand-dark font-semibold text-sm leading-relaxed">{line}</h3>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Upcoming Branches */}
      <section className="py-16 bg-white">
        <div className="container">
          <Reveal as="h2" className="text-2xl font-bold font-heading text-brand-dark text-center mb-10">Upcoming Branches at</Reveal>
          <div className="svc-branch-grid">
            {[
              { flag: '/assets/images/flags/Usa.png', name: 'USA' },
              { flag: '/assets/images/flags/india.png', name: 'india' },
            ].map(({ flag, name }, i) => (
              <Reveal key={name} delay={i * 120} className="svc-branch-item text-center py-6">
                <div className="svc-branch-flag rounded-full overflow-hidden mx-auto mb-4 border-4 border-brand/20 shadow-md">
                  <img src={flag} alt={name} className="w-full h-full object-cover" />
                </div>
                <h2 className="svc-branch-name text-brand-dark font-bold font-heading text-lg">{name}</h2>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}